import { useLocation, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const destinationSchema = z.object({
  to: z
    .string()
    .startsWith("/")
    .refine((path) => !path.startsWith("//") && !/[?#]/.test(path)),
  search: z.record(z.unknown()),
  hash: z.string(),
});
const cacheSchema = z.record(destinationSchema);

export type SidebarDestination = z.infer<typeof destinationSchema>;

function readCache(key: string): Partial<Record<string, SidebarDestination>> {
  try {
    const parsed = cacheSchema.safeParse(
      JSON.parse(localStorage.getItem(key) ?? "{}"),
    );
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

export function useSidebarPageCache(
  scope: string,
  section?: string,
  invalidPathPrefix?: string,
) {
  const location = useLocation();
  const isNavigating = useRouterState({
    select: (state) => state.isLoading || state.status === "pending",
  });
  const key = `lawworks:sidebar-pages:${scope}`;
  const [cache, setCache] = useState(() => ({ key, pages: readCache(key) }));

  const pages = useMemo(() => {
    const storedPages = cache.key === key ? cache.pages : readCache(key);
    if (!invalidPathPrefix) return storedPages;
    // Clear all subpages of a missing resource, even in another section's cache.
    return Object.fromEntries(
      Object.entries(storedPages).filter(
        ([, destination]) =>
          destination &&
          destination.to !== invalidPathPrefix &&
          !destination.to.startsWith(`${invalidPathPrefix}/`),
      ),
    );
  }, [cache, key, invalidPathPrefix]);

  useEffect(() => {
    const nextPages = { ...pages };
    if (section && !isNavigating) {
      nextPages[section] = {
        to: location.pathname,
        search: location.search,
        hash: location.hash,
      };
    }
    if (
      cache.key === key &&
      JSON.stringify(cache.pages) === JSON.stringify(nextPages)
    )
      return;

    setCache({ key, pages: nextPages });
    try {
      localStorage.setItem(key, JSON.stringify(nextPages));
    } catch {
      // Keep navigation memory for this session when browser storage is unavailable.
    }
  }, [
    cache,
    key,
    pages,
    section,
    isNavigating,
    location.pathname,
    location.search,
    location.hash,
  ]);

  return pages;
}
